import { AgGridReact } from 'ag-grid-react';
import React, { useCallback, useEffect, useMemo } from 'react';
import useFetchActionbarAuth from 'src/hooks/useFetchActionbarAuth';
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';
import useUiuxStore, { SAL6020_SAL6010OrderNavigationData, Tab } from 'src/store/useUiuxStore';
import { useLoadingSpinnerStore } from 'src/store/util/loadingSpinnerUtil';
import { downloadSettingExcel } from "src/utils/blob/blobUtil";
import { gridRefSelectedRowHandler } from 'src/utils/grid/GridHandlerContent';
import { showError, showSuccess, ShowSuccessFetch, showWarning } from 'src/utils/toast/toastUtil';
import { deleteSAL1010_R01, downloadExcel_SAL1010_R01, getSAL1010_R01AllCount, getSAL1010_R01DetailList, getSAL1010_R01HeaderList, getSAL1010_R01One, getPdfBlob_SAL1010_R01, patchSAL1010_R01, postSAL1010_R01 } from './Api_SAL1010_R01';
import { defaultSAL1010_R01DetailDto, defaultSAL1010_R01HeaderDto, SAL1010_R01DetailDto, SAL1010_R01HeaderDto } from './Dto_SAL1010_R01';
import { SAL1010_R01Filter, useSAL1010_R01Store } from './Store_SAL1010_R01';

import { calculateDetailAmount, filterDeletedItems, SALES_ORDER_DETAIL_AMOUNT_MAPPING } from 'src/utils/calculation/calculationUtils';
import { formatDate, validateString } from 'src/utils/string/stringUtil';
import { SAL1010_R01Dto } from './Dto_SAL1010_R01';
import { validateCustomCd, validateEmployeeCd } from 'src/utils/grid/GridValidateContent';
import { useCommonCodeValidate } from 'src/utils/commonCode/CommonCodeValidateContext';
import {
	buildCommonCodeRequirements,
	SAL1010_R01_HEADER_ROW_VALIDATE,
} from 'src/utils/commonCode/commonCodeRequirementConfig';
import { useQzPrint } from 'src/hooks/useQzPrint';
import qz from 'qz-tray';
import { applyOrderStatusOnly, applyOrderStatusToList, isOrderLocked } from 'src/utils/order/orderUtils';

interface Props {
	gridRef: React.RefObject<AgGridReact<SAL1010_R01HeaderDto> | null>;
	detailGridRef: React.RefObject<AgGridReact<SAL1010_R01DetailDto> | null>;
	
	tabList: Tab[] | undefined;
	setMessage: (message: string) => void;
	
	containerRef: React.RefObject<HTMLDivElement | null>;
	isResizing: React.MutableRefObject<boolean>;
	
}

export const Content_SAL1010_R01 = (props: Props) => {
	const {
		gridRef, detailGridRef,
		tabList, setMessage,
		containerRef,
		isResizing,
		
	} = props;

	const { setLoadingSpinner } = useLoadingSpinnerStore();
	const { validateCommonCodeLists } = useCommonCodeValidate();
	const {
		setCodeSearchModalProps,
		SAL6020_SAL6010OrderNavigationData,
		setSAL6020_SAL6010OrderNavigationData,
	} = useUiuxStore();
	const { printPdfFromBlob } = useQzPrint();

	const {
		SAL1010_R01Filter, setSAL1010_R01Filter,
		SAL1010_R01PastFilter, setSAL1010_R01PastFilter,
		SAL1010_R01List, setSAL1010_R01List,
		editSAL1010_R01, setEditSAL1010_R01,
		selectedSAL1010_R01, setSelectedSAL1010_R01,
		SAL1010_R01DetailList, setSAL1010_R01DetailList,
		deletedSAL1010_R01DetailList, setDeletedSAL1010_R01DetailList,
		selectedSAL1010_R01DetailList, setSelectedSAL1010_R01DetailList,
		originalSAL1010_R01DetailList, setOriginalSAL1010_R01DetailList,
		insertSAL1010_R01DetailList, updateSAL1010_R01DetailList, removeSAL1010_R01DetailList,
		addDeletedSAL1010_R01DetailList, removeDeletedSAL1010_R01DetailList,
		allCount, setAllCount,
		detailFetchTrigger, setDetailFetchTrigger,
		initData,
		setScrollTop, setDetailScrollTop,
	} = useSAL1010_R01Store();

	const { vatTypeList, moneyCdList, wmsCdList, compCdList, customCdList, employeeCdList, itemCdMap } = useCommonCodeStore();

	useFetchActionbarAuth("SAL1010_R01");

	// PDF 출력: QZ 연결 시 report1, 미연결/실패 시 새 탭 또는 브라우저 인쇄
	const handlePrintClick = useCallback(async () => {
		if (!selectedSAL1010_R01 || !selectedSAL1010_R01.salesOrderNo) {
			showWarning("출력할 수주를 선택하세요.");
			setMessage("출력할 수주를 선택하세요.");
			return;
		}
		setLoadingSpinner(true);
		try {
			const blob = await getPdfBlob_SAL1010_R01(selectedSAL1010_R01.salesOrderNo);
			if (qz.websocket.isActive()) {
				const ok = await printPdfFromBlob(blob, "report1");
				if (ok) {
					showSuccess("PDF 출력을 완료하였습니다.");
				} else {
					const fileURL = URL.createObjectURL(blob);
					window.open(fileURL, "_blank");
					showWarning(" PDF를 새 탭에서 열었습니다.");
				}
			} else {
				const fileURL = URL.createObjectURL(blob);
				window.open(fileURL, "_blank");
				showSuccess("PDF를 새 탭에서 열었습니다.");
			}
		} catch (error) {
			console.error("PDF 출력 중 오류 발생:", error);
			showError("PDF 출력 중 오류가 발생했습니다.");
			setMessage("PDF 출력 중 오류가 발생했습니다.");
		} finally {
			setLoadingSpinner(false);
		}
	}, [selectedSAL1010_R01, setMessage, printPdfFromBlob, setLoadingSpinner]);

	// DetailTable에서 내장 핸들러 사용으로 변경

	const handleExcelClick = useCallback(async () => {
		if (!selectedSAL1010_R01) {
			showWarning("선택된 수주내역이 없습니다.");
			return;
		}
		setLoadingSpinner(true);

		const response = await downloadExcel_SAL1010_R01(
			'',
			selectedSAL1010_R01.salesOrderNo,
			SAL1010_R01List.length ?? 0
		);

		downloadSettingExcel({ responseData: response.data.data, fileName: '수주정보등록-excel.xlsx' });
		setLoadingSpinner(false);
	}, [selectedSAL1010_R01, SAL1010_R01Filter, SAL1010_R01List]);

	const getParams = (filter: SAL1010_R01Filter) => {

		let orderStatusParam = filter.orderStatus;
		if(filter.orderStatus !== 'Y' && filter.orderStatus !== 'N') {
			orderStatusParam = '';
		}

		const params: Partial<SAL1010_R01Filter> = {
			compCd: filter.compCd || undefined,
			dateRange: filter.dateRange || undefined,
			customCd: filter.customCd || undefined,
			orderStatus: orderStatusParam || undefined,
		};
		return params;
	};

	const renewalAllCount = useCallback(async () => {
		const allCountResponse = await getSAL1010_R01AllCount(getParams(SAL1010_R01Filter));
		setAllCount(allCountResponse.data.data);
		return allCountResponse.data.data;
	}, [SAL1010_R01Filter, setAllCount]);

	// 목록 조회 
	const fetchData = useCallback(async () => {
		setScrollTop(null);
		setDetailScrollTop(null);
		setLoadingSpinner(true);

		const responseData = await getSAL1010_R01HeaderList(
			'',
			getParams(SAL1010_R01Filter),
			SAL1010_R01List.length ?? 0
		);

		let newList = [...responseData.data.data ] as SAL1010_R01HeaderDto[];
		setSAL1010_R01List(newList);

		const processAllCount = await renewalAllCount();
		if (newList && newList.length > 0) {
			gridRefSelectedRowHandler(
				gridRef,
				setSelectedSAL1010_R01,
				() => true, // 항상 첫 번째 행 선택
			);
			setSAL1010_R01PastFilter(SAL1010_R01Filter);
			ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총 ${newList.length}/${processAllCount}건)`);
			setTimeout(() => setMessage(""), 5000);
		} else {
			showWarning('데이터가 없습니다.');
			handleCreateClick();
		}
		
		setLoadingSpinner(false);
		return responseData;
	}, [SAL1010_R01Filter, SAL1010_R01List, setSAL1010_R01List, setSAL1010_R01PastFilter, setSelectedSAL1010_R01, selectedSAL1010_R01, setScrollTop, setDetailScrollTop]);

	const defaultSAL1010_R01HeaderRow = useCallback(() => {
		const logicCenter = editSAL1010_R01?.logicCenter || wmsCdList[0]?.minorCd || '';

		const commonResult = validateCommonCodeLists(
			buildCommonCodeRequirements(SAL1010_R01_HEADER_ROW_VALIDATE, {
				compCdList,
				moneyCdList,
				vatTypeList,
				wmsCdList,
			}),
		);
		if (!commonResult.valid) {
			return { ...defaultSAL1010_R01HeaderDto };
		}

		return {
			...defaultSAL1010_R01HeaderDto,
			compCd: compCdList[0].compCd,
			vatType: vatTypeList[0].minorCd,
			moneyCd: moneyCdList[0].minorCd,
			logicCenter,
		};
	}, [
		editSAL1010_R01, defaultSAL1010_R01HeaderDto,
		vatTypeList, moneyCdList, compCdList, wmsCdList,
		validateCommonCodeLists,
	]);

	const defaultSAL1010_R01DetailRow = useCallback(() => {
		return {
			...defaultSAL1010_R01DetailDto,
			inserted: true,
		}
	}, [defaultSAL1010_R01DetailDto]);

	const fetchOne = useCallback(async () => {
		if (!selectedSAL1010_R01) return;
		setLoadingSpinner(true);

		try {
			const response = await getSAL1010_R01One(selectedSAL1010_R01.salesOrderNo);
			setEditSAL1010_R01({
				...defaultSAL1010_R01HeaderRow,
				...response.data.data,
			});
		} catch (error) {
			console.error("조회 중 오류 발생:", error);
			showError("조회 중 오류가 발생했습니다.");
		}
		
		setLoadingSpinner(false);
	}, [selectedSAL1010_R01, setEditSAL1010_R01, setLoadingSpinner]);

	
	const fetchDetailList = useCallback(async (salesOrderNo: string) => {
		setDetailScrollTop(null);
		try {
			const responseData = await getSAL1010_R01DetailList(salesOrderNo);
			if (responseData.data.data && responseData.data.data.length > 0) {
				const detailList = responseData.data.data.map((value, idx) => calculateDetailAmount({
					...value,
					valueIndex: value.valueIndex ?? idx + 1,
					orderIndex: value.orderIndex ?? idx + 1,
					orderIndexSeq: value.orderIndexSeq ?? 0,
				}, SALES_ORDER_DETAIL_AMOUNT_MAPPING));
				
				setSAL1010_R01DetailList(detailList);
				setOriginalSAL1010_R01DetailList(detailList);
				setMessage(`${selectedSAL1010_R01?.customNm}의 상세내역 ${detailList.length}건을 조회하였습니다.`);
				setTimeout(() => setMessage(""), 5000);
			} else {
				showError('상세내역이 없습니다.');
				setSAL1010_R01DetailList([]);
				insertSAL1010_R01DetailList([{ ...defaultSAL1010_R01DetailRow() }])
			}
		} catch (error) {
			console.error('상세 조회 중 오류 발생:', error);
			showError('상세내역 조회 중 오류가 발생했습니다.');
		}
	}, [selectedSAL1010_R01, setSAL1010_R01DetailList, setOriginalSAL1010_R01DetailList, setMessage, setDetailScrollTop, insertSAL1010_R01DetailList, defaultSAL1010_R01DetailRow]);

	// 초기화 함수
	const initContainerData = useCallback(() => {
		initData();
		setDeletedSAL1010_R01DetailList([]);
		setSelectedSAL1010_R01DetailList([]);
		setEditSAL1010_R01({ ...defaultSAL1010_R01HeaderRow() });
	}, [compCdList, vatTypeList, moneyCdList, initData, setDeletedSAL1010_R01DetailList, setSelectedSAL1010_R01DetailList, setEditSAL1010_R01]);

	const handleDeleteClick = useCallback(() => {
		if (deletedSAL1010_R01DetailList.length > 0) {
			// 삭제 표시된 행이 있으면 전체 복구
			setDeletedSAL1010_R01DetailList([]);
			setSelectedSAL1010_R01DetailList([]);
			setSAL1010_R01DetailList([...SAL1010_R01DetailList]);
		} else if (selectedSAL1010_R01DetailList.length > 0) {
			// 삭제 표시된 행이 없으면 선택된 행만 삭제 표시
			setDeletedSAL1010_R01DetailList(selectedSAL1010_R01DetailList);
			setSelectedSAL1010_R01DetailList([]);
		}
	}, [selectedSAL1010_R01DetailList, deletedSAL1010_R01DetailList, SAL1010_R01DetailList, setDeletedSAL1010_R01DetailList, setSelectedSAL1010_R01DetailList, setSAL1010_R01DetailList]);

	// 신규 생성
	const handleCreateClick = () => {
		setSelectedSAL1010_R01(undefined);
		setEditSAL1010_R01({ ...defaultSAL1010_R01HeaderRow() });
		setSAL1010_R01DetailList([{ ...defaultSAL1010_R01DetailRow() }]);
		setDeletedSAL1010_R01DetailList([]);
		setSelectedSAL1010_R01DetailList([]);
		setOriginalSAL1010_R01DetailList([]);
		setDetailScrollTop(null);
	};

	const applyEstimateNavigationData = useCallback((navData: SAL6020_SAL6010OrderNavigationData) => {
		setSelectedSAL1010_R01(undefined);
		setDeletedSAL1010_R01DetailList([]);
		setSelectedSAL1010_R01DetailList([]);
		setDetailScrollTop(null);

		const orderDt = formatDate(navData.estimateDt || new Date());
		const compNm = compCdList.find((comp) => comp.compCd === navData.compCd)?.compNm || '';

		const detailList: SAL1010_R01DetailDto[] = navData.detailList.map((detail, idx) => ({
			...defaultSAL1010_R01DetailDto,
			valueIndex: idx + 1,
			orderIndex: idx + 1,
			orderIndexSeq: 0,
			compCd: navData.compCd,
			customCd: navData.customCd,
			customNm: navData.customNm,
			orderDt,
			itemSeq: idx + 1,
			itemCd: detail.itemCd,
			itemNm: detail.itemNm,
			itemSpec: detail.itemSpec,
			itemUnit: detail.estimateUnit,
			orderUnit: detail.estimateUnit,
			orderQty: detail.estimateQty,
			orderPrice: detail.estimatePrice,
			dcAmt: detail.dcAmt,
			itemAmt: detail.itemAmt,
			remark: detail.remark || '',
			inserted: true,
			isModified: false,
		}));

		const orderAmt = detailList.reduce((sum, detail) => sum + (Number(detail.itemAmt) || 0), 0);
		const selectedVatType = vatTypeList.find((vat) => vat.minorCd === navData.vatType);
		const vatAmt = Math.round(orderAmt * Number(selectedVatType?.ref01 || 0) / 100);
		const logicCenter = wmsCdList[0]?.minorCd || '';

		setEditSAL1010_R01({
			...defaultSAL1010_R01HeaderRow(),
			compCd: navData.compCd,
			compNm,
			customCd: navData.customCd,
			customNm: navData.customNm,
			orderDt,
			vatType: navData.vatType,
			moneyCd: navData.moneyCd,
			telNo: navData.telNo || '',
			faxNo: navData.faxNo || '',
			email: navData.email || '',
			post: navData.post || '',
			address: navData.address || '',
			detailAddress: navData.detailAddress || '',
			empNo: navData.empNo || '',
			empNm: navData.empNm || '',
			remark: navData.remark || '',
			logicCenter,
			orderAmt,
			vatAmt,
			totalAmt: orderAmt + vatAmt,
			inserted: true,
			isModified: false,
		});
		setSAL1010_R01DetailList(detailList);
		setOriginalSAL1010_R01DetailList([]);
		setMessage(`견적서(${navData.salesEstimateNo}) 데이터를 수주 등록 화면에 적용하였습니다.`);
		setTimeout(() => setMessage(''), 5000);
	}, [
		compCdList,
		vatTypeList,
		wmsCdList,
		defaultSAL1010_R01HeaderRow,
		defaultSAL1010_R01DetailDto,
		setSelectedSAL1010_R01,
		setDeletedSAL1010_R01DetailList,
		setSelectedSAL1010_R01DetailList,
		setDetailScrollTop,
		setEditSAL1010_R01,
		setSAL1010_R01DetailList,
		setOriginalSAL1010_R01DetailList,
		setMessage,
	]);

	// SAL6010 견적서 → 수주 등록 네비게이션 데이터 수신
	useEffect(() => {
		if (!SAL6020_SAL6010OrderNavigationData) return;

		applyEstimateNavigationData(SAL6020_SAL6010OrderNavigationData);
		setSAL6020_SAL6010OrderNavigationData(null);
	}, [
		SAL6020_SAL6010OrderNavigationData,
		applyEstimateNavigationData,
		setSAL6020_SAL6010OrderNavigationData,
	]);

	const validateInsertData = (header: SAL1010_R01HeaderDto, detailList: SAL1010_R01DetailDto[]) => {
		if (!validateString(header.compCd)) {
			showWarning('사업장을 선택해주세요.', 3000);
			return false;
		}
		if (!validateString(header.customCd)) {
			showWarning('거래처를 입력해주세요.', 3000);
			return false;
		}
		if (!validateCustomCd(header.customCd, customCdList)) {
			showWarning('거래처가 존재하지 않습니다.(입력칸을 더블클릭하거나 엔터키로 세부사항을 열어서 지정해주세요)'); return false;
		}
		if (header.orderDt === undefined) {
			showWarning('수주일자를 입력해주세요.', 3000);
			return false;
		}
		// if (!validateString(header.empNo)) {
		// 	showWarning('담당자를 선택해주세요.', 3000);
		// 	return false;
		// }
		if(validateString(header.empNo)) {
			if (!validateEmployeeCd(header.empNo, employeeCdList)) {
				showWarning('담당자가 존재하지 않습니다.(입력칸을 더블클릭하거나 엔터키로 세부사항을 열어서 지정해주세요)'); return false;
			}
		}
		if (!validateString(header.moneyCd)) {
			showWarning('통화를 선택해주세요.', 3000);
			return false;
		}
		if (!validateString(header.vatType)) {
			showWarning('부가세 유형을 선택해주세요.', 3000);
			return false;
		}
		if (!validateString(header.logicCenter)) {
			showWarning('물류센터를 선택해주세요.', 3000);
			return false;
		}
		if(header.dueDt === undefined) {
			showWarning('납기일자를 입력해주세요.', 3000);
			return false;
		}



		// 삭제되지 않은 상세 행들만 필터링하여 검증
		const activeDetailList = filterDeletedItems(detailList, deletedSAL1010_R01DetailList);

		if (activeDetailList.length === 0) {
			showWarning('상세내역을 입력해주세요.', 3000);
			return false;
		}

		// 상세 데이터 검증 (삭제되지 않은 행들만)
		for (const detail of activeDetailList) {
			if (!detail.itemCd) {
				showWarning('품목코드를 입력해주세요.', 3000);
				return false;
			}
			if (detail.orderQty <= 0) {
				showWarning('수량을 입력해주세요.', 3000);
				return false;
			}

			if(detail.cancelQty > detail.orderQty) {
				showWarning('취소수량은 주문량보다 클 수 없습니다.'); return false;
			}
			// if(detail.completeTransport > detail.orderQty) {
			// 	showWarning('출하완료수량은 주문량보다 클 수 없습니다.'); return false;
			// }
			if(detail.cancelQty + detail.completeTransport > detail.orderQty) {
				showWarning('출하완료수량과 취소수량의 합이 주문량보다 클 수 없습니다.'); return false;
			}
			if(detail.inserted === true && detail.completeTransport != 0) {
				showWarning('등록 작업에는 출하완료수량 입력을 할수 없습니다'); return false;
			}
			
			// 중복 품목코드 검증
			const otherDetailList = activeDetailList.filter((_, index) => 
				index !== activeDetailList.findIndex(d => d.valueIndex === detail.valueIndex)
			);
			if (otherDetailList.some(otherDetail => otherDetail.itemCd === detail.itemCd)) {
				showWarning(`중복된 품목코드가 있습니다.\n품목코드: ${detail.itemCd}`, 3000);
				return false;
			}
			if(!itemCdMap.has(detail.itemCd)) {
				showWarning('품목코드가 해당 MES의 품목정보에 존재하지 않습니다.\n품목코드: ' + detail.itemCd); return false;
			}
		}

		return true;
	};

	// 저장(등록/수정)
	const handleSaveClick = useCallback(async () => {
		if (!editSAL1010_R01) return;
		if (isOrderLocked(editSAL1010_R01.orderStatus)) {
			showWarning('출고가 진행된 수주는 수정할 수 없습니다.');
			return;
		}

		// 삭제된 상세 행들을 필터링 후 (수량 - 취소수량) × 단가 반영
		const activeDetailList = filterDeletedItems(SAL1010_R01DetailList, deletedSAL1010_R01DetailList)
			.map((detail) => calculateDetailAmount(detail, SALES_ORDER_DETAIL_AMOUNT_MAPPING));

		// 데이터 검증 (삭제되지 않은 행들만)
		if (!validateInsertData(editSAL1010_R01, SAL1010_R01DetailList)) {
			return;
		}

		setLoadingSpinner(true);

		let processName = '';
		let newDetailList: SAL1010_R01DetailDto[] = [];
		let newHeaderList: SAL1010_R01HeaderDto[] = [];
		let responseData: SAL1010_R01Dto = {
			salesOrderHeaderDto: editSAL1010_R01!,
			salesOrderDetailDtoList: SAL1010_R01DetailList
		};

		try {

			if (selectedSAL1010_R01) {
				// 기존 데이터 수정 시
				newDetailList = activeDetailList; // 이미 필터링된 리스트 사용

				if (newDetailList.length === 0) {
					// 모든 상세 행이 삭제된 경우 → 전체 삭제
					processName = 'delete';
					const deleteResponse = await deleteSAL1010_R01(selectedSAL1010_R01.salesOrderNo);

					newHeaderList = SAL1010_R01List.filter(value => 
						value.salesOrderNo !== selectedSAL1010_R01.salesOrderNo
					);

					if (deleteResponse && deleteResponse.data) {
						if (deleteResponse.data.successFlag) {
							showSuccess('수주내역이 삭제 되었습니다');
							setMessage('수주내역이 삭제 되었습니다');
						}
					}
				} else {
					// 일부 상세 행만 삭제된 경우 → PATCH로 수정
					processName = 'patch';
					const patchResponse = await patchSAL1010_R01({
						salesOrderHeaderDto: editSAL1010_R01,
						salesOrderDetailDtoList: newDetailList  // 삭제된 행 제외
					});

					if(!patchResponse || !patchResponse.data.successFlag) { setLoadingSpinner(false); return; }

					responseData = patchResponse.data.data[0];
					if (editSAL1010_R01!.orderStatus === 'E') {
						newHeaderList = SAL1010_R01List.filter(value => value.salesOrderNo !== editSAL1010_R01!.salesOrderNo);
					} else {
						newHeaderList = SAL1010_R01List.map(value => {
							if (value.salesOrderNo === responseData.salesOrderHeaderDto.salesOrderNo) {
								return responseData.salesOrderHeaderDto;
							}
							return value;
						});
					}

					if (patchResponse?.data?.data && patchResponse.data.successFlag) {
						showSuccess('수주내역이 수정 되었습니다');
						setMessage('수주내역이 수정 되었습니다');
					}
				}
			} else {
				// 신규 등록
				processName = 'post';
				const postResponse = await postSAL1010_R01({
					salesOrderHeaderDto: editSAL1010_R01,
					salesOrderDetailDtoList: activeDetailList
				});

				if(!postResponse || !postResponse.data.successFlag) { setLoadingSpinner(false); return; }

				responseData = postResponse.data.data[0];
				newHeaderList = [
					...SAL1010_R01List,
					responseData.salesOrderHeaderDto,
				] as SAL1010_R01HeaderDto[];

				if (postResponse?.data?.data && postResponse.data.successFlag) {
					showSuccess('수주내역이 등록 되었습니다');
					setMessage('수주내역이 등록 되었습니다');
				}
			}

		} catch (error: any) {
			console.log('error :', error.response?.data?.message || error.message);
			showError(error.response?.data?.message || '수주 작업 중 오류가 발생했습니다.');
			return;
		}

		// 저장 후 상태 업데이트
		if (processName === 'delete') {
			setSAL1010_R01List(newHeaderList);
			setSelectedSAL1010_R01(undefined);
			setSAL1010_R01DetailList([]);
			handleCreateClick();
		} else if (processName === 'patch' || processName === 'post') {
			const isCanceledSalesOrder = responseData.salesOrderHeaderDto.orderStatus === 'E' || responseData.salesOrderHeaderDto.orderStatus === 'C';
			if(isCanceledSalesOrder && SAL1010_R01Filter.orderStatus === 'N'){
				newHeaderList = SAL1010_R01List.filter(value => value.salesOrderNo !== responseData.salesOrderHeaderDto.salesOrderNo);
				setSAL1010_R01DetailList([]);
				handleCreateClick();
			}else{
				setSAL1010_R01DetailList(responseData.salesOrderDetailDtoList);
				setSelectedSAL1010_R01(responseData.salesOrderHeaderDto);
				setOriginalSAL1010_R01DetailList(responseData.salesOrderDetailDtoList);
				await fetchOneProcess(responseData.salesOrderHeaderDto.salesOrderNo);
			}
			setSAL1010_R01List(newHeaderList);
		}

		setDeletedSAL1010_R01DetailList([]);
		setSelectedSAL1010_R01DetailList([]);
		setTimeout(() => setMessage(''), 5000);

		setLoadingSpinner(false);

	}, [editSAL1010_R01, selectedSAL1010_R01, SAL1010_R01DetailList, deletedSAL1010_R01DetailList, SAL1010_R01List, setSAL1010_R01List, setSAL1010_R01DetailList, setSelectedSAL1010_R01, setOriginalSAL1010_R01DetailList, setDeletedSAL1010_R01DetailList, setSelectedSAL1010_R01DetailList, setMessage, handleCreateClick]);

	const fetchOneProcess = useCallback(async (salesOrderNo: string) => {
		await fetchOne();
		await fetchDetailList(salesOrderNo);
	}, [fetchOne, fetchDetailList])

	/** 탭 복귀 시 입력값/그리드는 유지하고 서버 orderStatus만 반영해 잠금 여부를 갱신 */
	const fetchOrderStatus = useCallback(async () => {
		const store = useSAL1010_R01Store.getState();
		const salesOrderNo = store.selectedSAL1010_R01?.salesOrderNo || store.editSAL1010_R01?.salesOrderNo;
		if (!salesOrderNo) return;

		try {
			const response = await getSAL1010_R01One(salesOrderNo);
			const orderStatus = response.data.data?.orderStatus;
			if (orderStatus === undefined) return;

			const latest = useSAL1010_R01Store.getState();
			if (latest.editSAL1010_R01?.salesOrderNo === salesOrderNo) {
				const nextEdit = applyOrderStatusOnly(latest.editSAL1010_R01, orderStatus);
				if (nextEdit && nextEdit !== latest.editSAL1010_R01) {
					setEditSAL1010_R01(nextEdit);
				}
			}
			if (latest.selectedSAL1010_R01?.salesOrderNo === salesOrderNo) {
				const nextSelected = applyOrderStatusOnly(latest.selectedSAL1010_R01, orderStatus);
				if (nextSelected && nextSelected !== latest.selectedSAL1010_R01) {
					setSelectedSAL1010_R01(nextSelected);
				}
			}
			const nextList = applyOrderStatusToList(
				latest.SAL1010_R01List,
				(row) => row.salesOrderNo === salesOrderNo,
				orderStatus,
			);
			if (nextList !== latest.SAL1010_R01List) {
				setSAL1010_R01List(nextList);
			}
		} catch (error) {
			console.error('수주 상태 조회 실패:', error);
		}
	}, [setEditSAL1010_R01, setSelectedSAL1010_R01, setSAL1010_R01List]);

	// HomeContainer는 activeTab만 렌더하므로, 출고 화면으로 가면 이 컴포넌트는 언마운트된다.
	// 다시 들어오면 mount 시점에 orderStatus만 조회한다. fetchOne은 폼/그리드를 덮어써서 사용하지 않는다.
	useEffect(() => {
		fetchOrderStatus();
	}, [fetchOrderStatus]);

	// 수주내역 상세조회
	useEffect(() => {
		const selectedChangeProcess = async () => {
			if (!detailFetchTrigger) return;

			(detailGridRef.current as any)?.clearRange?.();

			if (!selectedSAL1010_R01?.salesOrderNo) {
				setDetailFetchTrigger(false);
				return;
			}

			const salesOrderNo = selectedSAL1010_R01.salesOrderNo;
			await fetchOneProcess(salesOrderNo);
			setDetailFetchTrigger(false);
		}
		selectedChangeProcess();
	}, [selectedSAL1010_R01, fetchOneProcess, detailFetchTrigger, setDetailFetchTrigger])

	// 상세 데이터가 없을 때 기본 행 추가
	useEffect(() => {
		if (selectedSAL1010_R01 && SAL1010_R01DetailList.length === 0) {
			setSAL1010_R01DetailList([{ ...defaultSAL1010_R01DetailRow() }]);
		}
	}, [selectedSAL1010_R01, SAL1010_R01DetailList.length, setSAL1010_R01DetailList]);

	return {
		handlePrintClick,
		handleExcelClick,
		fetchData,
		defaultSAL1010_R01HeaderRow,
		defaultSAL1010_R01DetailRow,
		initContainerData,
		handleDeleteClick,
		handleCreateClick,
		handleSaveClick,
	};
}; 